import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuthClient, useLogout } from "Authentication/react";

import urls from "utilities/urls";

const SESSION_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours (in ms)
const MODAL_SHOW_TIME = 90 * 60 * 1000; // 90 minutes (in ms)
const INTERVAL_TIME = 5 * 1000; // 5 seconds (in ms)
const LOCAL_STORAGE_KEY = "beat";

export interface UseTimeoutProvider {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  onLogout: () => void;
}

export const useTimeoutProvider = (): UseTimeoutProvider => {
  // external hooks
  const navigate = useNavigate();
  const location = useLocation();
  const client = useAuthClient();
  const logout = useLogout();

  // local state (dialog)
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // refs
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const localBeat = useRef<number>(0);
  const setLocalBeat = () => (localBeat.current = new Date().getTime());

  // localStorage
  const getSharedBeat = useCallback(
    (): number => parseInt(localStorage.getItem(LOCAL_STORAGE_KEY) ?? "0", 10),
    []
  );
  const setSharedBeat = useCallback((value: number = new Date().getTime()) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, value.toString());
  }, []);

  // functions
  const onContinue = useCallback(() => {
    setSharedBeat();
    setIsOpen(false);
  }, [setSharedBeat]);

  const onLogout = useCallback(() => {
    setSharedBeat(0);
    setIsOpen(false);
    async function run() {
      await logout();
      navigate(urls.login());
    }

    void run();
  }, [navigate, logout, setSharedBeat]);

  const onTick = useCallback(() => {
    const isAuthenticated = client?.isAuthenticated();
    const sharedBeat = getSharedBeat();
    const timeDiff =
      new Date().getTime() - Math.max(sharedBeat, localBeat.current);

    switch (true) {
      case !isAuthenticated || sharedBeat === 0 || timeDiff > SESSION_TIMEOUT:
        // Session expired
        setSharedBeat(0);
        onLogout();
        break;

      case timeDiff > MODAL_SHOW_TIME && !isOpen:
        // Show session timeout modal
        setIsOpen(true);
        break;

      case timeDiff < MODAL_SHOW_TIME && isOpen:
        // Hide session timeout modal
        setIsOpen(false);
        break;

      default:
        // Session still active
        if (localBeat.current > sharedBeat) {
          setSharedBeat(localBeat.current);
        }
        break;
    }
  }, [isOpen, client, getSharedBeat, setSharedBeat, onLogout]);

  // useEffects

  // initialize local beat on mount
  useEffect(() => {
    setSharedBeat();
    setLocalBeat();
  }, [setSharedBeat]);

  // start the interval to check for timeout
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      onTick();
    }, INTERVAL_TIME);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [onTick]);

  useEffect(() => {
    const handleWindowEvents = () => {
      if (!isOpen && getSharedBeat() > 0) {
        setLocalBeat();
      }
    };

    const controller = new AbortController();

    // listen for specific window events to ensure user is still active
    window.addEventListener("mousemove", handleWindowEvents, {
      signal: controller.signal,
    });
    window.addEventListener("keydown", handleWindowEvents, {
      signal: controller.signal,
    });
    window.addEventListener("click", handleWindowEvents, {
      signal: controller.signal,
    });
    window.addEventListener("scroll", handleWindowEvents, {
      signal: controller.signal,
    });

    //cleanup function
    return () => {
      controller.abort();
    };
  }, [navigate, location.pathname, isOpen, getSharedBeat]);

  return {
    isOpen: isOpen,
    onClose: () => setIsOpen(false),
    onContinue,
    onLogout,
  };
};
